import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { neoStreamApi } from 'src/components/Neo/neoStreamApi'

export type OrgRole = 'OWNER' | 'ADMIN' | 'MEMBER'
export type OrgStatus = 'ACTIVE' | 'SUSPENDED' | 'BANNED'

export type OrganizationDto = {
  id: string
  name: string
  slug: string
  status: OrgStatus
  createdById: string
  personalForUserId: string | null
  claimedEmailDomain: string | null
  createdAt: string
  updatedAt: string
}

export const ORGANIZATIONS_QUERY_KEY = ['neo-organizations'] as const
export const organizationDetailQueryKey = (orgId: string): readonly unknown[] =>
  ['neo-organization', orgId] as const

// Match the project-wide React Query convention used in useCompany /
// useConnectors / useLocations — orgs change rarely, 5-min staleness is
// fine and avoids re-fetch storms from the SettingsScreen.
const STALE_MS = 5 * 60 * 1000

export function useOrganizations(): ReturnType<
  typeof useQuery<OrganizationDto[]>
> {
  return useQuery<OrganizationDto[]>({
    queryKey: ORGANIZATIONS_QUERY_KEY,
    queryFn: async () => {
      const { data } = await neoStreamApi.get<{
        organizations: OrganizationDto[]
      }>('/neo/organizations')
      return data.organizations
    },
    staleTime: STALE_MS,
  })
}

export function useOrganization(
  orgId: string | undefined,
): ReturnType<typeof useQuery<OrganizationDto>> {
  return useQuery<OrganizationDto>({
    queryKey: orgId
      ? organizationDetailQueryKey(orgId)
      : ['neo-organization', 'disabled'],
    queryFn: async () => {
      const { data } = await neoStreamApi.get<{
        organization: OrganizationDto
      }>(`/neo/organizations/${orgId}`)
      return data.organization
    },
    enabled: typeof orgId === 'string' && orgId.length > 0,
    staleTime: STALE_MS,
  })
}

export function useCreateTeamOrganization(): ReturnType<
  typeof useMutation<OrganizationDto, Error, { name: string }>
> {
  const queryClient = useQueryClient()
  return useMutation<OrganizationDto, Error, { name: string }>({
    mutationFn: async (input) => {
      const { data } = await neoStreamApi.post<{
        organization: OrganizationDto
      }>('/neo/organizations', input)
      return data.organization
    },
    onSuccess: () => {
      // Invalidate the user's org list so the new team appears in switchers.
      void queryClient.invalidateQueries({ queryKey: ORGANIZATIONS_QUERY_KEY })
    },
  })
}

export function selectPersonalOrganization(
  orgs: OrganizationDto[] | undefined,
): OrganizationDto | undefined {
  return orgs?.find((org) => org.personalForUserId !== null)
}

export function selectTeamOrganizations(
  orgs: OrganizationDto[] | undefined,
): OrganizationDto[] {
  return orgs?.filter((org) => org.personalForUserId === null) ?? []
}
